/**
 * exportBundleDownloads — one client entry point per export format, all
 * consuming the neutral {@link LayoutExportBundle}. KMZ/shapefile/DXF build
 * fully in the browser; DWG posts our own generated DXF to the authed
 * `/api/cad/export` route (LibreDWG dwgwrite) and falls back to advising a
 * DXF download when the converter is unavailable.
 */
import JSZip from 'jszip';

import { authedFetch } from '../../lib/authedFetch';
import type { LayoutExportBundle } from './layoutExportBundle';
import { exportBundleAsShapefile } from './shapefileWriter';
import { exportBundleAsWorldDxf } from './worldDxfExporter';

export type StudioExportFormat = 'kmz' | 'shapefile' | 'dxf' | 'dwg';

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Minimal, deterministic KML from the bundle (one placemark per feature). */
export function bundleKml(bundle: LayoutExportBundle): string {
  const placemarks = bundle.features.flatMap(feature => {
    let points = feature.geometry.coordinates.filter(point => (
      Number.isFinite(point.lat)
      && Number.isFinite(point.lng)
      && point.lat >= -90
      && point.lat <= 90
      && point.lng >= -180
      && point.lng <= 180
    ));
    if (feature.geometry.type === 'polygon') {
      const first = points[0];
      const last = points.at(-1);
      if (first && last && first.lat === last.lat && first.lng === last.lng) {
        points = points.slice(0, -1);
      }
      const distinct = new Set(points.map(point => `${point.lat},${point.lng}`));
      if (distinct.size < 3 || !first) return [];
      points = [...points, { ...points[0]! }];
    } else if (
      (feature.geometry.type === 'line' && points.length < 2)
      || (feature.geometry.type === 'point' && points.length < 1)
    ) {
      return [];
    }
    const coordinates = points
      .map(point => `${point.lng},${point.lat},0`)
      .join(' ');
    const innerHoles = feature.geometry.type === 'polygon'
      ? (feature.geometry.holes ?? [])
          .map(hole => {
            let hPoints = hole.filter(point => (
              Number.isFinite(point.lat)
              && Number.isFinite(point.lng)
              && point.lat >= -90
              && point.lat <= 90
              && point.lng >= -180
              && point.lng <= 180
            ));
            const hFirst = hPoints[0];
            const hLast = hPoints.at(-1);
            if (hFirst && hLast && hFirst.lat === hLast.lat && hFirst.lng === hLast.lng) {
              hPoints = hPoints.slice(0, -1);
            }
            const hDistinct = new Set(hPoints.map(point => `${point.lat},${point.lng}`));
            if (hDistinct.size < 3 || !hFirst) return '';
            const closed = [...hPoints, { ...hPoints[0]! }];
            const hCoords = closed.map(point => `${point.lng},${point.lat},0`).join(' ');
            return `<innerBoundaryIs><LinearRing><coordinates>${hCoords}</coordinates></LinearRing></innerBoundaryIs>`;
          })
          .filter(Boolean)
          .join('')
      : '';
    const geometry = feature.geometry.type === 'polygon'
      ? `<Polygon><outerBoundaryIs><LinearRing><coordinates>${coordinates}</coordinates></LinearRing></outerBoundaryIs>${innerHoles}</Polygon>`
      : feature.geometry.type === 'line'
        ? `<LineString><coordinates>${coordinates}</coordinates></LineString>`
        : `<Point><coordinates>${coordinates}</coordinates></Point>`;
    const data = {
      id: feature.id,
      kind: feature.kind,
      category: feature.category,
      source: feature.source,
      ...(feature.properties ?? {}),
    };
    const extendedData = Object.entries(data)
      .map(([key, value]) => `<Data name="${escapeXml(key)}"><value>${escapeXml(String(value ?? ''))}</value></Data>`)
      .join('');
    return [`<Placemark id="${escapeXml(feature.id)}"><name>${escapeXml(feature.name)}</name>`
      + `<description>${escapeXml(`${feature.category} · ${feature.kind}${feature.areaAcres != null ? ` · ${feature.areaAcres.toFixed(2)} ac` : ''} · ${feature.source}`)}</description>`
      + `<ExtendedData>${extendedData}</ExtendedData>`
      + `${geometry}</Placemark>`];
  }).join('\n');
  return '<?xml version="1.0" encoding="UTF-8"?>\n'
    + '<kml xmlns="http://www.opengis.net/kml/2.2"><Document>\n'
    + `<name>${escapeXml(bundle.name)}</name>\n`
    + `${placemarks}\n`
    + '</Document></kml>\n';
}

export async function downloadBundleAsKmz(bundle: LayoutExportBundle): Promise<string> {
  if (bundle.features.length === 0) throw new Error('Nothing to export: the bundle has no drawable features.');
  const zip = new JSZip();
  zip.file('doc.kml', bundleKml(bundle));
  zip.file('manifest.json', JSON.stringify(bundle, null, 2));
  const blob = await zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.google-earth.kmz',
    compression: 'DEFLATE',
  });
  const filename = `${bundle.name}.kmz`;
  triggerDownload(blob, filename);
  return filename;
}

export async function downloadBundleAsShapefile(bundle: LayoutExportBundle): Promise<string> {
  const exported = await exportBundleAsShapefile(bundle);
  triggerDownload(exported.blob, exported.filename);
  return exported.filename;
}

export function downloadBundleAsDxf(bundle: LayoutExportBundle): string {
  const exported = exportBundleAsWorldDxf(bundle);
  triggerDownload(new Blob([exported.dxf], { type: 'application/dxf' }), exported.filename);
  return exported.filename;
}

export class DwgExportUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DwgExportUnavailableError';
  }
}

export async function downloadBundleAsDwg(bundle: LayoutExportBundle): Promise<string> {
  const exported = exportBundleAsWorldDxf(bundle);
  const response = await authedFetch('/api/cad/export', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/dxf',
      'X-Cad-Export-Name': encodeURIComponent(bundle.name),
    },
    body: exported.dxf,
  });
  if (response.status === 503 || response.status === 404) {
    throw new DwgExportUnavailableError(
      'The DWG converter is not available on this server — use the DXF export instead.',
    );
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error || `DWG export failed (${response.status}).`);
  }
  const blob = await response.blob();
  const filename = `${bundle.name}.dwg`;
  triggerDownload(blob, filename);
  return filename;
}
